/**
 * ReversePaymentDialog.jsx — Muhasebe FAZ 6.8, "Ödemeyi Geri Al" akışı.
 *
 * Uç: POST /admin/payments/{payment_id}/reverse  Body: { reason } (min 10 karakter)
 * 201 -> `payment_reversals` (status="pending_approval") — PARA HAREKET ETMEZ,
 * yalnızca başka bir yöneticinin onaylayacağı bir talep açılır. Para gerçek
 * anlamda `POST /admin/payment-reversals/{id}/approve` çağrıldığında hareket
 * eder (DebtLedgerSubTab.jsx'teki "Onay Bekliyor" popover'ı).
 *
 * İki adımlı `AlertDialog` (PLAN §11.5 BAĞLAYICI, `Sheet` DEĞİL):
 *   1/2 "Gerekçe"     — ödeme özeti + uyarı + zorunlu gerekçe (min 10 karakter)
 *   2/2 "Son Onay"    — girilen gerekçe salt-okunur, geri dönüş YOK
 *
 * `AlertDialogAction`/`AlertDialogCancel` KULLANILMAZ (alert-dialog.jsx:72-83
 * `preventDefault` çağrılmazsa dialog'u otomatik kapatır) — footer'da
 * `noteUiKit.ActionButton` kullanılır, `AlertDialog*` primitive'leri yalnızca
 * yapı + a11y (role="alertdialog", focus trap, Escape) için kalır.
 *
 * `reversePaymentErrorMessage`: `noteUiKit.noteErrorMessage` ile AYNI kademe
 * (yerel `accounting.errors.<code>` -> yoksa backend `message_tr`/`message_en`
 * -> yoksa jenerik fallback), TEK farkla: `prim_donemi_kapali` için yerel
 * anahtar HİÇ eklenmedi/aranmadı — mesaj dinamik (hangi dönemin kapalı olduğunu
 * söylüyor), statik metin bunu ezerse bilgi kaybolur. Bu yardımcı
 * DebtLedgerSubTab.jsx'teki Onayla/Reddet aksiyonlarında da kullanılır.
 *
 * Senetli taksit ters kaydı (senetli taksit tahsilatının geri alınma akışı
 * turu, 2026-09-28) — backend `_assert_not_note_linked`'in artık BLOKLAMADIĞI
 * senaryo (kind=installment tahsilatı bağlı bir senedi I-2 ile 'collected'a
 * çekmişti): `row.note_linked === true` iken 1/2 adımda EK bir bilgilendirme
 * (`note_linked_warning`) gösterilir — "taksit yeniden açılır, bağlı senet
 * aktif duruma döner". `row.method === 'moka'` iken (Moka kaynaklı tahsilat,
 * `collection_rows[].method = payment_method || provider`, Moka akışı ikisini
 * de "moka" yazar) AYRICA bir uyarı (`moka_warning`) gösterilir — Moka'daki
 * ödeme bu akıştan iade EDİLMEZ, yalnızca kayıt düzeltmesi yapılır (backend
 * `payment_reversals.moka_iade_yapilmaz` alanıyla AYNI karar, engelleme
 * DEĞİL).
 *
 * KARAR DEĞİŞİKLİĞİ (2026-09-28, reviewer bulgusu — "senetli tahsilatı geri
 * almanın TEK net yolu olsun"): doğrudan senet tahsilatının (kind=promissory)
 * ters kaydı da backend'de ARTIK BLOKLU DEĞİL (payment_reversal_service.py::
 * execute_reversal, `_assert_not_note_linked` gerekçesi). Bu yüzden
 * `senetli_tahsilat_geri_alinamaz` inline-uyarı özel dalı (artık hiçbir zaman
 * dönmeyen bir backend hatasına özel UI dalıydı) KALDIRILDI — tüm hatalar
 * artık standart toast ile gösterilir (aşağıdaki `handleConfirm`).
 *
 * KAPSAM GENİŞLEMESİ (fix/moka-profil-link devamı, 2026-09-29): satır-bazlı
 * tetikleyici (`columnDefs.js::reversePaymentActionCell`) `kind === 'promissory'`
 * satırlarında da artık buton gösterir. Bu dosyada `row.kind === 'promissory'`
 * iken (`isDirectNote`) `note_linked_warning` ile AYNI desende ayrı bir
 * `direct_note_warning` InlineAlert'i gösterilir — "bu, senedin KENDİ tahsilat
 * kaydı, geri alındığında senet yeniden active'e döner" bilgisi `isNoteLinked`
 * (kind=installment + I-2 ile kapanmış senet) durumundan FARKLI bir satır
 * sınıfını kapsar, ikisi AYNI ANDA true olamaz (biri installment ucundan,
 * diğeri doğrudan senet ucundan gelir).
 *
 * Muhasebe FAZ 4 (Tur 2, 2026-09-29, feature/parasut-fatura-faz4-web) —
 * `parasutStatus` prop'u (DebtLedgerSubTab.jsx'in TOPLU çektiği
 * usePaymentsInvoiceStatusBulk haritasından `row.id` ile bulunan tekil
 * kayıt) `status === "done"` ise EK bir `parasut_invoice_warning` gösterilir
 * — geri alma İŞLEMİ ENGELLENMEZ, yalnızca "Paraşüt'teki fatura otomatik
 * düzeltilmez" bilgisi verilir.
 */
import { useEffect, useRef, useState } from 'react';
import { Undo2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/contexts/ToastContext';
import { getApiErrorCode, getApiErrorMessage } from '@/utils/apiError';
import { fmtMoneyAccounting } from '@/utils/money';
import { useReversePayment } from '@/api/queries/accounting';
import { ActionButton, Field, INPUT_CLASS, InlineAlert } from './noteUiKit';
import { fmtLedgerDate } from './columnDefs';

const REASON_MIN_LENGTH = 10;

/**
 * Backend `error_code` -> yerel `accounting.errors.*` çevirisi; `noteErrorMessage`
 * ile AYNI kademe ama `prim_donemi_kapali` İSTİSNASI ile: bu kod için yerel
 * anahtar hiç aranmaz, HER ZAMAN backend'in `message_tr`/`message_en`'i gösterilir.
 */
export function reversePaymentErrorMessage(err, lang, t) {
  const code = getApiErrorCode(err);
  if (code && code !== 'prim_donemi_kapali') {
    const key = `accounting.errors.${code}`;
    const localized = t(key);
    if (localized !== key) return localized;
  }
  return getApiErrorMessage(err, lang);
}

export default function ReversePaymentDialog({ open, onOpenChange, userId, row, parasutStatus }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const reversePayment = useReversePayment(userId);

  const [step, setStep] = useState('form');
  const [reason, setReason] = useState('');
  const [attempted, setAttempted] = useState(false);
  const titleRef = useRef(null);

  const submitting = reversePayment.isPending;

  // Dialog her açılışta sıfırlanır — önceki denemenin gerekçesi/hatası kalmaz.
  useEffect(() => {
    if (open) {
      setStep('form');
      setReason('');
      setAttempted(false);
    }
  }, [open]);

  // Adım değişince başlığa programatik focus — SR adım geçişini duysun.
  useEffect(() => {
    if (open) titleRef.current?.focus();
  }, [open, step]);

  if (!row) return null;

  const reasonTooShort = reason.trim().length < REASON_MIN_LENGTH;
  // Senetli taksit ters kaydı turu — backend `collection_rows[].note_linked`
  // (routes/payments.py::admin_student_accounting_ledger) bu tahsilatın
  // bağlı bir senedi I-2 ile 'collected'a çektiğini bildirir.
  const isNoteLinked = row.note_linked === true;
  // Doğrudan senet tahsilatı (kind=promissory) — kapsam genişlemesi, bkz.
  // dosya başı docstring'i. `isNoteLinked` ile AYNI anda true OLAMAZ (biri
  // installment ucundan I-2 ile kapanan senedi, diğeri senedin KENDİ
  // tahsilat kaydını işaret eder).
  const isDirectNote = row.kind === 'promissory';
  // `method` = payment_method || provider (backend); Moka akışı ikisini de
  // "moka" yazar (services/moka/collect.py).
  const isMoka = row.method === 'moka';
  // Muhasebe FAZ 4 (Tur 2, feature/parasut-fatura-faz4-web) — bu tahsilat
  // için Paraşüt faturası ZATEN kesilmişse (status="done") admin'i uyar.
  // Geri alma İŞLEMİ ENGELLENMEZ (backend de engellemiyor) — yalnızca
  // bilgilendirme: Paraşüt'teki fatura OTOMATİK iptal/düzeltilmez.
  const hasIssuedInvoice = parasutStatus?.found && parasutStatus.status === 'done';

  const handleContinue = () => {
    setAttempted(true);
    if (reasonTooShort) return;
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (submitting) return;
    try {
      await reversePayment.mutateAsync({ paymentId: row.id, reason: reason.trim() });
      toast.success(t('accounting.reverse_payment.request_success'));
      onOpenChange(false);
    } catch (err) {
      const message = reversePaymentErrorMessage(err, lang, t);
      toast.error(message);
      // Adım 2'de AÇIK kalır, gerekçe kaybolmaz.
    }
  };

  const handleOpenChange = (v) => {
    if (submitting) return;
    onOpenChange(v);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent
        className="rounded-2xl border-2 border-[var(--border-light)] max-w-md bg-white space-y-4"
        data-testid="reverse-payment-dialog"
      >
        {step === 'form' ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle ref={titleRef} tabIndex={-1} className="font-display text-lg font-bold text-[var(--text-main)] outline-none">
                {t('accounting.reverse_payment.title')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('accounting.reverse_payment.payment_summary')
                  .replace('{amount}', fmtMoneyAccounting(row.amount, lang))
                  .replace('{date}', fmtLedgerDate(row.date, lang))}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <InlineAlert variant="warning" testId="reverse-payment-warning">
              {t('accounting.reverse_payment.warning')}
            </InlineAlert>

            {isNoteLinked && (
              <InlineAlert variant="info" testId="reverse-payment-note-linked-warning">
                {t('accounting.reverse_payment.note_linked_warning')}
              </InlineAlert>
            )}

            {isDirectNote && (
              <InlineAlert variant="info" testId="reverse-payment-direct-note-warning">
                {t('accounting.reverse_payment.direct_note_warning')}
              </InlineAlert>
            )}

            {isMoka && (
              <InlineAlert variant="warning" testId="reverse-payment-moka-warning">
                {t('accounting.reverse_payment.moka_warning')}
              </InlineAlert>
            )}

            {hasIssuedInvoice && (
              <InlineAlert variant="warning" testId="reverse-payment-parasut-invoice-warning">
                {t('accounting.reverse_payment.parasut_invoice_warning')}
              </InlineAlert>
            )}

            <Field
              id="reverse-payment-reason"
              label={t('accounting.reverse_payment.reason_label')}
              required
              hint={t('accounting.reverse_payment.reason_counter').replace('{count}', String(reason.trim().length))}
              error={attempted && reasonTooShort ? t('accounting.reverse_payment.reason_min_error') : ''}
            >
              {({ id, describedBy }) => (
                <textarea
                  id={id}
                  rows={3}
                  autoFocus
                  className={INPUT_CLASS}
                  value={reason}
                  placeholder={t('accounting.reverse_payment.reason_placeholder')}
                  aria-required
                  aria-invalid={attempted && reasonTooShort ? true : undefined}
                  aria-describedby={describedBy}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="reverse-payment-reason-input"
                />
              )}
            </Field>

            <AlertDialogFooter className="gap-2 mt-2">
              <ActionButton
                variant="neutral"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                testId="reverse-payment-cancel-btn"
              >
                {t('admin.btn_cancel')}
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={handleContinue}
                disabled={submitting}
                testId="reverse-payment-continue-btn"
              >
                {t('accounting.reverse_payment.continue_button')}
              </ActionButton>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle ref={titleRef} tabIndex={-1} className="font-display text-lg font-bold text-[var(--text-main)] outline-none">
                {t('accounting.reverse_payment.second_confirm_title')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('accounting.reverse_payment.second_confirm_desc')}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <InlineAlert variant="info" testId="reverse-payment-reason-readonly">
              {reason}
            </InlineAlert>

            <AlertDialogFooter className="gap-2 mt-2">
              <ActionButton
                variant="neutral"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                testId="reverse-payment-cancel-btn"
              >
                {t('admin.btn_cancel')}
              </ActionButton>
              <ActionButton
                variant="danger"
                icon={Undo2}
                loading={submitting}
                disabled={submitting}
                onClick={handleConfirm}
                testId="reverse-payment-confirm-btn"
              >
                {submitting ? t('accounting.reverse_payment.submitting') : t('accounting.reverse_payment.confirm_button')}
              </ActionButton>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
